import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  LockClosedIcon,
  PlayCircleIcon,
  BookOpenIcon,
  VideoCameraIcon,
  AcademicCapIcon,
  CalculatorIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import './Learning.css';

const NODE_ICONS = {
  article: BookOpenIcon,
  video: VideoCameraIcon,
  quiz: AcademicCapIcon,
  simulation: CalculatorIcon,
};

const LEVEL_LABELS = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

const RoadmapView = ({ userId }) => {
  const { categoryId } = useParams();
  const [roadmapData, setRoadmapData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadRoadmap();
  }, [categoryId, userId]);

  const loadRoadmap = async () => {
    try {
      setLoading(true);
      const params = userId ? { user_id: userId } : {};
      const response = await axios.get(`/api/learning/categories/${categoryId}/roadmap`, {
        params,
      });
      setRoadmapData(response.data);
    } catch (error) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = (node) => {
    if (node.status === 'locked') return;
    setSelectedNode(node);
  };

  const handleStartNode = async (node) => {
    if (userId) {
      try {
        await axios.post('/api/learning/progress/start', null, {
          params: { user_id: userId, category_id: categoryId, node_id: node.id },
        });
      } catch (error) {
        /* ignore */
      }
    }
    navigate(`/learning/${categoryId}/node/${node.id}`);
  };

  const groupNodesByLevel = (nodes) => {
    const grouped = { beginner: [], intermediate: [], advanced: [] };
    nodes.forEach((node) => {
      if (grouped[node.level]) grouped[node.level].push(node);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading roadmap…</p>
      </div>
    );
  }

  if (!roadmapData) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <p className="text-body text-primary mb-4">Category not found</p>
          <button type="button" onClick={() => navigate('/learning')} className="btn-secondary">
            Back to learning hub
          </button>
        </div>
      </div>
    );
  }

  const groupedNodes = groupNodesByLevel(roadmapData.nodes);

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate('/learning')}
          className="flex items-center gap-2 text-secondary hover:text-primary mb-6 transition-colors duration-300"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span className="text-body">Back to learning hub</span>
        </button>

        <div className="flex items-start justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">{roadmapData.category.icon}</span>
              <h1 className="font-display text-h1 text-primary">{roadmapData.category.name}</h1>
            </div>
            <p className="text-body text-secondary max-w-prose">{roadmapData.category.description}</p>
          </div>

          <div className="surface-panel p-5 text-center min-w-[140px]">
            <p className="figure font-display text-h1 text-primary tabular-nums mb-1">
              {roadmapData.progress.percentage}%
            </p>
            <p className="text-label text-secondary">
              <span className="figure tabular-nums">{roadmapData.progress.completed}</span> /{' '}
              <span className="figure tabular-nums">{roadmapData.progress.total}</span> completed
            </p>
            <div className="progress-bar-track mt-3">
              <div
                className="progress-bar-fill"
                style={{ width: `${roadmapData.progress.percentage}%` }}
              />
            </div>
          </div>
        </div>

        <div className="space-y-12">
          {Object.entries(groupedNodes).map(([level, nodes]) => {
            if (nodes.length === 0) return null;

            return (
              <section key={level}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-label text-pine font-medium px-3 py-1 border border-pine rounded-md bg-pine-tint">
                    {LEVEL_LABELS[level] || level}
                  </span>
                  <div className="flex-1 h-px bg-hairline" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {nodes.map((node, index) => {
                    const IconComponent = NODE_ICONS[node.type] || BookOpenIcon;
                    const isLocked = node.status === 'locked';
                    const isCompleted = node.status === 'completed';
                    const isInProgress = node.status === 'in_progress';

                    let nodeClass = 'roadmap-node';
                    if (isLocked) nodeClass += ' roadmap-node-locked';
                    else if (isCompleted) nodeClass += ' roadmap-node-completed';
                    else if (isInProgress) nodeClass += ' roadmap-node-current';

                    return (
                      <motion.div
                        key={node.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.03 }}
                        className={nodeClass}
                        onClick={() => handleNodeClick(node)}
                        role="button"
                        tabIndex={isLocked ? -1 : 0}
                        onKeyDown={(e) => {
                          if (!isLocked && (e.key === 'Enter' || e.key === ' ')) handleNodeClick(node);
                        }}
                      >
                        <div className="absolute top-4 right-4">
                          {isCompleted && <CheckCircleIcon className="w-5 h-5 text-pine" />}
                          {isInProgress && <PlayCircleIcon className="w-5 h-5 text-pine" />}
                          {isLocked && <LockClosedIcon className="w-5 h-5 text-faint" />}
                        </div>

                        <div className="text-2xl mb-3">{node.icon}</div>

                        <div className="flex items-center gap-2 mb-2">
                          <IconComponent className="w-4 h-4 text-secondary" />
                          <span className="text-label text-secondary capitalize">{node.type}</span>
                        </div>

                        <h3 className="font-display text-h3 text-primary mb-2">{node.title}</h3>
                        <p className="text-label text-secondary mb-4 line-clamp-2">{node.description}</p>

                        <div className="flex items-center justify-between">
                          <span className="text-label text-faint flex items-center gap-1">
                            <ClockIcon className="w-3.5 h-3.5" />
                            {node.duration}
                          </span>
                          {!isLocked && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartNode(node);
                              }}
                              className="text-label text-pine hover:underline"
                            >
                              {isCompleted ? 'Review' : isInProgress ? 'Continue' : 'Start'} →
                            </button>
                          )}
                        </div>

                        {node.prerequisites?.length > 0 && (
                          <p className="text-label text-faint mt-3 pt-3 border-t border-hairline">
                            {node.prerequisites.length} prerequisite
                            {node.prerequisites.length > 1 ? 's' : ''}
                          </p>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <AnimatePresence>
          {selectedNode && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setSelectedNode(null)}
            >
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="surface-panel p-8 max-w-2xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-6 gap-4">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">{selectedNode.icon}</span>
                    <div>
                      <h2 className="font-display text-h2 text-primary mb-2">{selectedNode.title}</h2>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="badge-mark badge-mark-earned capitalize">
                          {LEVEL_LABELS[selectedNode.level] || selectedNode.level}
                        </span>
                        <span className="text-label text-faint flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          {selectedNode.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedNode(null)}
                    className="icon-btn text-faint"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <p className="text-body text-secondary mb-6">{selectedNode.description}</p>

                <button type="button" onClick={() => handleStartNode(selectedNode)} className="btn-primary w-full">
                  {selectedNode.status === 'completed' ? 'Review lesson' : 'Start learning'}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RoadmapView;
